import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "hv.search";
const SESSION_KEY = "hv.session";

const defaultSearch = {
  type: "stays",
  locationId: "",
  locationLabel: "",
  locationSlug: "",
  locationType: "city",
  checkin: "",
  checkout: "",
  guests: 2,
  adults: 2,
  children: 0,
  infants: 0,
  pets: false,
};

const clampInt = (value, { min = 0, max = 50, fallback = 0 } = {}) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, Math.round(num)));
};

const clampGuests = (value) => clampInt(value, { min: 1, max: 50, fallback: 2 });

const parseBool = (value) => {
  if (value === true || value === false) return value;
  const raw = String(value || "").toLowerCase();
  if (!raw) return false;
  return raw === "true" || raw === "1" || raw === "yes";
};

const getStored = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const setStored = (value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
};

const getSessionId = () => {
  if (typeof window === "undefined") return "";
  const existing = window.localStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const next = `sess_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
  window.localStorage.setItem(SESSION_KEY, next);
  return next;
};

const normalizeType = (value) => {
  const raw = String(value || "").toLowerCase().trim();
  if (raw === "stay") return "stays";
  if (raw === "service" || raw === "services") return "packages";
  if (raw === "stays" || raw === "packages" || raw === "cabs") return raw;
  return "stays";
};

const parseFromQuery = (search) => {
  const params = new URLSearchParams(search || "");
  const type = normalizeType(params.get("type") || "stays");
  const locationId = params.get("location_id") || "";
  const locationLabel = params.get("location") || params.get("destination") || "";
  const locationSlug = params.get("location_slug") || params.get("locationSlug") || "";
  const locationType = params.get("location_type") || params.get("locationType") || "city";
  const checkin = params.get("checkin") || params.get("checkIn") || "";
  const checkout = params.get("checkout") || params.get("checkOut") || "";
  const adultsParam = params.get("adults");
  const childrenParam = params.get("children");
  const infantsParam = params.get("infants");
  const petsParam = params.get("pets");
  const guestsParam = params.get("guests");
  const adults = clampInt(adultsParam, { min: 1, max: 50, fallback: guestsParam ? clampGuests(guestsParam) : 2 });
  const children = clampInt(childrenParam, { min: 0, max: 20, fallback: 0 });
  const infants = clampInt(infantsParam, { min: 0, max: 10, fallback: 0 });
  const pets = parseBool(petsParam);
  const guests = clampGuests(adults + children || guestsParam || 2);
  const hasValues = Boolean(
    locationId ||
      locationLabel ||
      locationSlug ||
      checkin ||
      checkout ||
      params.get("type") ||
      params.get("adults") ||
      params.get("children") ||
      params.get("infants")
  );
  return {
    hasValues,
    value: {
      type,
      locationId,
      locationLabel,
      locationSlug,
      locationType,
      checkin,
      checkout,
      guests,
      adults,
      children,
      infants,
      pets,
    },
  };
};

const buildQuery = (state) => {
  const params = new URLSearchParams();
  if (state?.type) {
    const typeValue = state.type === "stays" ? "stay" : state.type;
    params.set("type", typeValue);
  }
  if (state?.locationId) params.set("location_id", state.locationId);
  if (state?.locationLabel) params.set("location", state.locationLabel);
  if (state?.locationSlug) params.set("location_slug", state.locationSlug);
  if (state?.locationType) params.set("location_type", state.locationType);
  if (state?.checkin) params.set("checkin", state.checkin);
  if (state?.checkout) params.set("checkout", state.checkout);
  if (state?.adults !== undefined) params.set("adults", String(state.adults));
  if (state?.children !== undefined) params.set("children", String(state.children));
  if (state?.infants !== undefined) params.set("infants", String(state.infants));
  if (state?.pets !== undefined) params.set("pets", state.pets ? "true" : "false");
  const totalGuests =
    state?.guests ??
    (Number(state?.adults || 0) + Number(state?.children || 0) || undefined);
  if (totalGuests) params.set("guests", String(totalGuests));
  return params;
};

const SearchContext = createContext({
  search: defaultSearch,
  setSearch: () => {},
  clearSearch: () => {},
  sessionId: "",
  buildQueryString: () => "",
});

export const SearchProvider = ({ children }) => {
  const location = useLocation();
  const [searchState, setSearchState] = useState(() => {
    const stored = getStored();
    return stored ? { ...defaultSearch, ...stored, type: normalizeType(stored.type) } : defaultSearch;
  });
  const [sessionId] = useState(() => getSessionId());

  useEffect(() => {
    const { hasValues, value } = parseFromQuery(location.search);
    if (hasValues) {
      setSearchState((prev) => {
        const next = { ...prev, ...value };
        setStored(next);
        return next;
      });
      return;
    }
    const stored = getStored();
    if (stored) {
      setSearchState((prev) => {
        const next = { ...prev, ...stored, type: normalizeType(stored.type) };
        setStored(next);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const setSearch = useCallback((next) => {
    setSearchState((prev) => {
      const merged = { ...prev, ...next };
      const type = normalizeType(merged.type);
      const adults = clampInt(merged.adults ?? merged.guests, { min: 1, max: 50, fallback: 2 });
      const children = clampInt(merged.children, { min: 0, max: 20, fallback: 0 });
      const infants = clampInt(merged.infants, { min: 0, max: 10, fallback: 0 });
      const guests = clampGuests(merged.guests ?? adults + children);
      const normalized = {
        ...merged,
        type,
        locationType: merged.locationType || "city",
        adults,
        children,
        infants,
        pets: parseBool(merged.pets),
        guests,
      };
      setStored(normalized);
      return normalized;
    });
  }, []);

  const clearSearch = useCallback(() => {
    setSearchState(defaultSearch);
    setStored(defaultSearch);
  }, []);

  const buildQueryString = useCallback(
    (override = {}) => {
      const merged = { ...searchState, ...override };
      return buildQuery(merged).toString();
    },
    [searchState]
  );

  const value = useMemo(
    () => ({ search: searchState, setSearch, clearSearch, sessionId, buildQueryString }),
    [searchState, setSearch, clearSearch, sessionId, buildQueryString]
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export const useSearchContext = () => useContext(SearchContext);
